import * as React from "react"
import { Link, useLocation } from "react-router-dom"
import { useAuthStore } from "@/store"
import { Shield, LogOut, User, Settings, MoreVertical, ChevronLeft } from "lucide-react"

const sections = [
  { title: "TELEGRAM", items: [
    { name: "Bots", path: "/bots" },
    { name: "Channels", path: "/channels" },
    { name: "Commands", path: "/commands" },
    { name: "Messages", path: "/messages" },
    { name: "Menus", path: "/menus" },
  ]},
  { title: "VPN MANAGEMENT", items: [
    { name: "Servers", path: "/servers" },
    { name: "Nodes", path: "/nodes" },
    { name: "Subscriptions", path: "/subscriptions" },
    { name: "Plans", path: "/plans" },
    { name: "Configs", path: "/configs" },
  ]},
  { title: "USERS & ACCESS", items: [
    { name: "Users", path: "/users" },
    { name: "Roles & Permissions", path: "/permissions" },
    { name: "Referrals", path: "/referrals" },
  ]},
  { title: "BILLING", items: [
    { name: "Payments", path: "/payments" },
    { name: "Transactions", path: "/transactions" },
    { name: "Plans & Pricing", path: "/pricing" },
    { name: "Coupons", path: "/coupons" },
  ]},
  { title: "PLATFORM", items: [
    { name: "Settings", path: "/settings" },
    { name: "Features", path: "/features" },
    { name: "Plugins", path: "/plugins" },
    { name: "Secrets", path: "/secrets" },
    { name: "Themes", path: "/themes" },
  ]},
  { title: "MONITORING", items: [
    { name: "Logs", path: "/logs" },
    { name: "Queue", path: "/queue" },
    { name: "Events", path: "/events" },
    { name: "Metrics", path: "/metrics" },
  ]},
]

export default function Sidebar({ collapsed: controlledCollapsed, onCollapse }) {
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const [openMenu, setOpenMenu] = React.useState(null)
  const [internalCollapsed, setInternalCollapsed] = React.useState(false)
  const collapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed
  const [expandedSections, setExpandedSections] = React.useState(['TELEGRAM'])

  const toggleCollapse = () => {
    const newState = !collapsed
    setInternalCollapsed(newState)
    onCollapse?.(newState)
  }

  const toggleSection = (title) => {
    setExpandedSections(prev =>
      prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]
    )
  }

  return (
    <aside style={{
      width: collapsed ? 72 : 240,
      background: '#111827',
      borderRight: '1px solid #1F2937',
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      position: 'fixed',
      left: 0,
      top: 0,
      zIndex: 40,
      transition: 'width 200ms ease-out',
      overflow: 'hidden',
    }}>
      {/* Logo + Collapse */}
      <div style={{ padding: collapsed ? '24px 0' : '24px', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Shield size={24} color="#3B82F6" />
          {!collapsed && <span style={{ fontSize: 16, fontWeight: 700, color: '#F8FAFC', whiteSpace: 'nowrap' }}>KouroshShield</span>}
        </div>
        {!collapsed && (
          <button onClick={toggleCollapse} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#64748B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChevronLeft size={16} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: collapsed ? '0 8px' : '0 12px' }}>
        {/* Dashboard */}
        <Link to="/" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 12,
          padding: collapsed ? '10px 0' : '10px 12px',
          borderRadius: 10,
          fontSize: 14,
          fontWeight: 500,
          marginBottom: collapsed ? 16 : 32,
          transition: 'all 180ms',
          background: location.pathname === '/' ? '#2563EB' : 'transparent',
          color: location.pathname === '/' ? '#fff' : '#F8FAFC',
          textDecoration: 'none',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
          {!collapsed && "Dashboard"}
        </Link>

        {/* Sections */}
        {sections.map((section) => {
          const isExpanded = expandedSections.includes(section.title)
          const hasActive = section.items.some(item => location.pathname === item.path)

          return (
            <div key={section.title} style={{ marginBottom: 8 }}>
              <button
                onClick={() => collapsed ? (toggleCollapse(), setExpandedSections([section.title])) : toggleSection(section.title)}
                title={collapsed ? section.title : undefined}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: collapsed ? 'center' : 'space-between',
                  padding: collapsed ? '8px 0' : '8px 12px',
                  background: 'none',
                  border: 'none',
                  color: hasActive ? '#3B82F6' : '#64748B',
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  transition: 'all 180ms',
                }}
              >
                {collapsed ? (
                  <section.icon size={18} />
                ) : (
                  <>
                    {section.title}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms' }}><path d="M6 9l6 6 6-6"/></svg>
                  </>
                )}
              </button>
              {!collapsed && (
                <div style={{ maxHeight: isExpanded ? 500 : 0, overflow: 'hidden', transition: 'max-height 250ms ease-out' }}>
                  {section.items.map((item) => (
                    <Link key={item.path} to={item.path} style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: 40,
                      padding: '0 12px',
                      borderRadius: 10,
                      fontSize: 13,
                      color: location.pathname === item.path ? '#fff' : '#94A3B8',
                      background: location.pathname === item.path ? '#2563EB' : 'transparent',
                      textDecoration: 'none',
                      transition: 'all 180ms',
                      marginLeft: 8,
                      marginRight: 8,
                      marginBottom: 2,
                    }}>
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* User */}
      <div style={{ padding: collapsed ? '12px 8px' : '12px', borderTop: '1px solid #1F2937' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: collapsed ? '8px 0' : 12, borderRadius: 10, justifyContent: collapsed ? 'center' : 'flex-start', cursor: 'pointer', position: 'relative' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
            {user?.username?.charAt(0)?.toUpperCase() || 'A'}
          </div>
          {!collapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#F8FAFC', whiteSpace: 'nowrap' }}>{user?.username || 'Admin'}</div>
                <div style={{ fontSize: 11, color: '#64748B', whiteSpace: 'nowrap' }}>{user?.role || 'Admin'}</div>
              </div>
              <button onClick={() => setOpenMenu(openMenu === 'user' ? null : 'user')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <MoreVertical size={16} color="#64748B" />
              </button>
            </>
          )}
          {openMenu === 'user' && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpenMenu(null)} />
              <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 8, background: '#1E293B', border: '1px solid #334155', borderRadius: 10, padding: 4, zIndex: 50 }}>
                <Link to="/profile" onClick={() => setOpenMenu(null)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, fontSize: 13, color: '#94A3B8', textDecoration: 'none' }}>Profile</Link>
                <Link to="/settings" onClick={() => setOpenMenu(null)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, fontSize: 13, color: '#94A3B8', textDecoration: 'none' }}>Settings</Link>
                <div style={{ height: 1, background: '#334155', margin: '4px 0' }} />
                <button onClick={() => { setOpenMenu(null); logout() }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, fontSize: 13, color: '#EF4444', background: 'none', border: 'none', width: '100%', cursor: 'pointer' }}>Sign Out</button>
              </div>
            </>
          )}
        </div>
      </div>
    </aside>
  )
}
